import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SectorMenu from './SectorMenu';
import externalLinks from './externalLinks.json';
import Home from './Home';
import Pcpn from './Pcpn';
import AcisPcpnMaps from './AcisPcpnMaps';
import UsgsGroundwater from './UsgsGroundwater';
import UsgsStreamflow from './UsgsStreamflow';
import Usdm from './Usdm';
import Sport from './Sport';
import Grace from './Grace';
import Eddi from './Eddi';
import ChamplainGraph from './ChamplainGraph';
import DsciGraph from './DsciGraph';
import BasicMap from './BasicMap';
import Publications from './Publications';
import Drought99 from './Drought99';
import FlashDrought from './FlashDrought';
import LearnMoreIntro from './LearnMoreIntro';
import UsdmHistory from './UsdmHistory';

const drawerWidth = 260;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        },
      },
    ],
  }),
);

const AppBar = styled(MuiAppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    variants: [
      {
        props: ({ open }) => open,
        style: {
          width: `calc(100% - ${drawerWidth}px)`,
          marginLeft: `${drawerWidth}px`,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      },
    ],
  })
);

const DrawerHeader = styled('div')(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  })
);

//------------------------------
//------------------------------

export default function PersistentDrawerInterface(props) {
  const { mode } = props;
  const [ open, setOpen ] = useState(true);
  const [ spiSaved, setSpiSaved ] = useState({markers:null, date:null, mapspi:"30-day", showdm:false});
  const [ gwSaved, setGwSaved ] = useState({markers:null, date:null, showdm:false});
  const [ sfSaved, setSfSaved ] = useState({markers:null, date:null, period:"7-Day", showdm:false});
  const [ searchParams ] = useSearchParams();
  const [ productParam, setProductParam ] = useState("home");

  const productList = ['home','eddi','pcpn','acispcpn','sport','grace','gw','sf','champlain','nwssnow',
                       'usdmabout','vtdec','vtpws','vtdoh','cmor','dir','cocoreps','cdc','airnow','cyanbac',
                       'nceiterm','fireea','agsurvey','pubs','kbdi','firedanger','flashcdi','firenwsbtv','firenwsnat',
                       'fireincident','raws','fire2024','sfcast','nohrsc','ladgtest','foresthealth','drought1999','dohcli',
                       'd3','drought2025','aboutflash','vegdri','quickdri','nidisvt','learnmoreintro','usdmpast','usdmdsci',
                       'vemhome','vemprepare','vemplans','vemresources','vemdash','nowcast','usdahelp','usdaflp','usdaelap',
                       'usdm','usdmchng1','usdmchng2','usdmchng4','masscare','airdec','sfprob','nrccswe',"epapublic","epaprivate",
                       "usgs2020","storymap2025","usdmhistory"]

  const NotFound = () => {
    // catch all for route not defined
    return (
      <div>
        <Typography variant="h6">404 - Page Not Found</Typography>
        <Typography>The page you are looking for does not exist.</Typography>
      </div>
    );
  };

  const ToIframe = ({ iframeSource }) => {
    // open page in an iframe
    return (
      <div style={{ width: '100%', height: '100vh' }}>
        <iframe 
          src = {iframeSource} 
          title = {"External Content from " + iframeSource}
          style = {{ border: 'none', width: '100%', height: '100%' }} 
        />
      </div>
    );
  };

  const ExternalLinkButton = ({ text }) => {
    // notify new window opened and add button to reopen external link in new tab
    const handleButtonClick = (url) => {
        window.open(url, "_blank", "noopener, noreferrer");
    };
    return (
      <>
        <Typography
          variant='body1'
          sx={{ my: 2 }}
        >
          &ldquo;{text}&rdquo; was opened in a new tab.
        </Typography>
        <Button 
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() => { handleButtonClick(externalLinks[productParam]) }}
        >
          Click to reopen
        </Button>
        </>
    );
  };

  const toggleDrawerOpen = () => {
    // toggle status of navigation panel (drawer)
    setOpen(!open);
  };
  
  useEffect(() => {
    // open display panel if "product" query parameter is present in URL
    const productParam = searchParams.get("product") || "home";
    setProductParam(productParam);
  }, [searchParams]);


  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawerOpen}
            edge="start"
            sx={[{ mr: 2 }, open && { display: 'none' }]}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ fontWeight:600, textAlign:"center", width:"100%" }}
          >
            Vermont Drought Dashboard
          </Typography>
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader>
            <IconButton onClick={toggleDrawerOpen}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <Link to={"?product=home"}>
            <Button variant="text" size="large" sx={{ mt:2 }}>Home</Button>
          </Link>
          <SectorMenu />
        </Drawer>
      </nav>

      <Main open={open}>
        <DrawerHeader />
        {productParam === 'vegdri' && <ExternalLinkButton text="Vegetation Drought Response Index (VegDRI)" />}
        {productParam === 'quickdri' && <ExternalLinkButton text="Quick Drought Response Index (QuickDRI)" />}
        {productParam === 'agsurvey' && <ExternalLinkButton text="Agriculture Drought Impact Survey (VAAFM)" />}
        {productParam === 'sfcast' && <ExternalLinkButton text="USGS River DroughtCast" />}
        {productParam === 'nwssnow' && <ExternalLinkButton text="Snow Information Page (NWS/NERFC)" />}
        {productParam === 'nohrsc' && <ExternalLinkButton text="Regional Snow Analyses" />}
        {productParam === 'vtdec' && <ExternalLinkButton  text="Vermont Drought Reports (VT DEC)" />}
        {productParam === 'vtpws' && <ExternalLinkButton text="Vermont Public Water System Drought Reports (VT DEC)" />}    
        {productParam === 'vtdoh' && <ExternalLinkButton  text="Drought and Your Well (VT DOH)" />}
        {productParam === 'fireea' && <ExternalLinkButton text="Eastern Area Fire Environmental Outlook" />}
        {productParam === 'firenwsbtv' && <ExternalLinkButton text="NWS BTV Fire Weather" />}
        {productParam === 'fireincident' && <ExternalLinkButton text="NIFC Wildland Fire Incident Locations" />}
        {productParam === 'firenwsnat' && <ExternalLinkButton text="NWS National Fire Weather" />}
        {productParam === 'fire2024' && <ExternalLinkButton text="Vermont Wildland Fires 2024" />}
        {productParam === 'raws' && <ExternalLinkButton text="RAWS Data" />}  
        {productParam === 'cdc' && <ExternalLinkButton text="Drought and Your Health (CDC)" />}
        {productParam === 'masscare' && <ExternalLinkButton text="VT Human Services-Mass Care video" />}
        {productParam === 'airnow' && <ExternalLinkButton text="AirNow Air Quality Index" />}
        {productParam === 'airdec' && <ExternalLinkButton text="VT DEC Air Monitoring Network" />}
        {productParam === 'nowcast' && <ExternalLinkButton text="NowCast Air Quality Index" />}
        {productParam === 'cyanbac' && <ExternalLinkButton text="Cyanobacteria Tracker" />}
        {productParam === 'cmor' && <ExternalLinkButton text="Condition Monitoring Observer Reports" />}
        {productParam === 'dir' && <ExternalLinkButton text="Drought Impact Reporter Dashboard (NDMC)" />}
        {productParam === 'cocoreps' && <ExternalLinkButton text="CoCoRaHS Condition Monitoring Reports" />}
        {productParam === 'dohcli' && <ExternalLinkButton text="Climate and Health (VT DOH)"/>}
        {productParam === 'usdmpast' && <ExternalLinkButton text="Past Impacts Across Vermont" />}              
        {productParam === 'nceiterm' && <ExternalLinkButton text="Current Drought Reduction (NCEI)" />}
        {productParam === 'ladgtest' && <ExternalLinkButton text="Lesley-Ann Dupigny-Giroux Testimony" />}
        {productParam === 'foresthealth' && <ExternalLinkButton text="Vermont Forest Health Highlights - 2025" />}
        {productParam === 'vemhome' && <ExternalLinkButton text="Vermont Emergency Management home page"  />}
        {productParam === 'vemprepare' && <ExternalLinkButton text="Vermont Emergency Management Community Preparedness page" />}
        {productParam === 'vem;lans' && <ExternalLinkButton text="Vermont Emergency Management Local Hazard Mitigation Plans page" />}
        {productParam === 'vemsources' && <ExternalLinkButton text="Vermont Emergency Management Local Hazard Mitigation Planning Resources"  />}
        {productParam === 'vemdash' && <ExternalLinkButton text="Vermont Emergency Management Geospatial Dashboard"  />}
        {productParam === 'usdahelp' && <ExternalLinkButton text="USDA Drought Resources"  />}
        {productParam === 'usdalfp' && <ExternalLinkButton text="Livestock Forage Disaster Program (LFP)"  />}
        {productParam === 'usdaelap' && <ExternalLinkButton text="Emergency Assistance for Livestock, Honeybees, and Farm-Raised Fish (ELAP)" />}
        {productParam === 'nidisvt' && <ExternalLinkButton text="NIDIS - Vermont" />}
        {productParam === 'sfprob' && <ExternalLinkButton text="USGS Streamflow Probabilities" />}
        {productParam === 'nrccswe' && <ExternalLinkButton text="NRCC Snow Water Equivalent" />}
        {productParam === 'epapublic' && <ExternalLinkButton text="EPA Tap Talk - Public Water Systems" />}
        {productParam === 'epaprivate' && <ExternalLinkButton text="EPA Tap Talk - Private Wells" />}
        {productParam === 'usgs2020' && <ExternalLinkButton text="2020 New England Drought Summary" />}
        {productParam === 'storymap2025' && <ExternalLinkButton text="2025 Vermont Drought Storymap" />}
        {productParam === 'd3' && <ExternalLinkButton text="D3 Drought Data Dashboard" />}
        {productParam === 'home' && <Home />}
        {productParam === 'acispcpn' && <AcisPcpnMaps />}
        {productParam === 'sport' && <Sport />}
        {productParam === 'grace' && <Grace />}
        {productParam === 'eddi' && <Eddi />}
        {productParam === 'champlain' && <ChamplainGraph />}
        {productParam === 'aboutflash' && <FlashDrought />}
        {productParam === 'pubs' && <Publications />}
        {productParam === 'learnmoreintro' && <LearnMoreIntro />}
        {productParam === 'usdmdsci' && <DsciGraph />}
        {productParam === 'drought1999' && <Drought99 />}
        {productParam === 'usdmhistory' && <UsdmHistory />}
        {productParam === 'usdmabout' && <ToIframe iframeSource="https://droughtmonitor.unl.edu/About/AbouttheData/DroughtClassification.aspx" />}
        {productParam === 'pcpn' && <Pcpn 
              spiSaved={spiSaved}
              setSpiSaved={setSpiSaved}
              mode={mode}
            />}
        {productParam === 'sf' && <UsgsStreamflow 
              sfSaved={sfSaved}
              setSfSaved={setSfSaved}
              mode={mode}
            />}
        {productParam === 'gw' && <UsgsGroundwater 
              gwSaved={gwSaved}
              setGwSaved={setGwSaved}
              mode={mode}
            />}
        {productParam === 'kbdi' && <BasicMap
              mapurl="https://nedews.nrcc.cornell.edu/NRCC_maps/kbdi_vt_current.png" 
              mapTitle="Keetch-Byram Drought Index Status"
            />}
        {productParam === 'firedanger' && <BasicMap 
              mapurl="https://anrmaps.vermont.gov/websites/firedanger/VermontFireDanger.jpg?_gl=1*1a5102e*_ga*MTcxODk1OTUxNi4xNzU2NDgyMDc3*_ga_V9WQH77KLW*czE3NTc1MTAyOTkkbzUkZzEkdDE3NTc1MTE2NzAkajExJGwwJGgw"
              mapTitle="Fire Danger Forecast"
              foottext="Visit <a href='https://fpr.vermont.gov/forest/wildland-fire/monitoring-fire-danger'>https://fpr.vermont.gov/forest/wildland-fire/monitoring-fire-danger</a> for more information."
            />}
        {productParam === 'flashcdi' && <BasicMap
              mapurl="https://ndmcblends.unl.edu/cdi/version_3/Current/flash_drought_condition_Current.png" 
              mapTitle="Flash Drought Conditions Indicator (NDMC)"
            />}
         {productParam === 'usdm' && <Usdm 
              mappng="data/png/current/current_vt_trd.png"
            />}
        {productParam === 'usdmchng1' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_1W.png"
            />}
        {productParam === 'usdmchng2' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_2W.png"
            />}
        {productParam === 'usdmchng4' && <Usdm 
              mappng="data/chng/png/current/current_VT_chng_4W.png"
            />}
        {productParam === 'drought2025' &&
          <div style={{height:'85vh'}}>
            <iframe 
              src="/vt/assets/Drought_2025/2025_Drought.pdf"
              width="100%" 
              height="100%" 
              title="Vermont PDF"
            />
          </div>
        }
        {!productList.includes(productParam) && <NotFound />} 
      </Main>
    </Box>
  );
}